import { BrandLockup } from '@/components/brand/BrandMark';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useCompany } from '@/hooks/use-company';
import { usePermissions } from '@/hooks/use-permissions';
import { getApiErrorMessage } from '@/lib/api/error';
import { cn } from '@/lib/utils';
import { Building2, ChevronDown, ChevronRight, Menu } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { CompanySwitcherPanel } from './CompanySwitcherPanel';
import {
  getNavigationSections,
  isNavigationItemActive,
  isNavigationSectionActive,
  type NavigationSection,
} from './navigation-config';
export function MobileNav() {
  const { activeCompany, userCompanies, setActiveCompany } = useCompany();
  const { canAccess } = usePermissions();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const currentLocation = `${pathname}${search}`;
  const [open, setOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const sections = getNavigationSections({ activeCompany, canAccess });

  const getSectionStateKey = (sectionId: string) => `${pathname}:${sectionId}`;

  const toggleSection = (section: NavigationSection, nextOpen: boolean) => {
    setOpenSections((current) => ({
      ...current,
      [getSectionStateKey(section.id)]: nextOpen,
    }));
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-sidebar-foreground lg:hidden"
          aria-label="Abrir menu principal"
          aria-expanded={open}
        >
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="bg-sidebar border-sidebar-border flex h-dvh max-h-dvh w-[90vw] max-w-sm flex-col overflow-hidden p-0"
      >
        <SheetHeader className="border-sidebar-border border-b px-5 py-5">
          <SheetTitle className="text-sidebar-foreground flex items-center gap-2 text-left">
            <BrandLockup textClassName="text-sidebar-foreground text-xl" />
          </SheetTitle>
        </SheetHeader>

        <div className="border-sidebar-border border-b px-5 py-5">
          <button
            type="button"
            onClick={() => setCompanyOpen((current) => !current)}
            className="bg-sidebar-muted/65 border-sidebar-border/70 flex w-full items-center justify-between rounded-2xl border px-4 py-4 shadow-sm"
          >
            <div className="flex items-center gap-3">
              <div className="bg-primary/20 flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl">
                {activeCompany?.profilePhoto ? (
                  <img
                    src={activeCompany.profilePhoto}
                    alt={activeCompany.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Building2 className="text-primary h-4 w-4" />
                )}
              </div>
              <div className="text-left">
                <p className="text-sidebar-foreground max-w-50 truncate text-sm font-semibold">{activeCompany?.name}</p>
                <p className="text-sidebar-foreground/70 mt-0.5 text-xs">Empresa ativa</p>
              </div>
            </div>
            <ChevronDown
              className={cn('text-sidebar-foreground/70 h-4 w-4 transition-transform', companyOpen && 'rotate-180')}
            />
          </button>

          {companyOpen && (
            <CompanySwitcherPanel
              className="mt-3"
              companies={userCompanies}
              activeCompanyId={activeCompany?.id}
              createLabel="Adicionar empresa"
              onSelectCompany={(company) => {
                void setActiveCompany(company)
                  .then(() => {
                    setCompanyOpen(false);
                    setOpen(false);
                  })
                  .catch((error) => {
                    toast.error('Erro ao trocar empresa', {
                      description: getApiErrorMessage(error, 'Erro ao trocar empresa.'),
                    });
                  });
              }}
              onCreateCompany={() => {
                setOpen(false);
                navigate('/onboarding?mode=create-company');
              }}
            />
          )}
        </div>

        <nav className="min-h-0 flex-1 touch-pan-y scrollbar-thin overflow-y-auto overscroll-contain px-5 py-6 pb-8">
          <div className="space-y-2">
            {sections.map((section) => {
              const isActive = isNavigationSectionActive(currentLocation, section);

              if (section.path) {
                return (
                  <NavLink
                    key={section.id}
                    to={section.path}
                    onClick={() => setOpen(false)}
                    className={cn(
                      'flex items-center gap-3 rounded-2xl px-2.5 py-2.5 text-sm font-semibold transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-sidebar-foreground hover:bg-sidebar-muted/60',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                        isActive ? 'bg-primary-foreground/15' : 'text-sidebar-foreground/78',
                      )}
                    >
                      <section.icon className="h-4.5 w-4.5" />
                    </span>
                    <span className="truncate">{section.label}</span>
                  </NavLink>
                );
              }

              const sectionStateKey = getSectionStateKey(section.id);
              const isOpen = openSections[sectionStateKey] ?? isActive;
              return (
                <Collapsible
                  key={section.id}
                  open={isOpen}
                  onOpenChange={(nextOpen) => toggleSection(section, nextOpen)}
                >
                  <div
                    className={cn(
                      'rounded-2xl',
                      isActive && 'bg-sidebar-muted/60 ring-sidebar-border/70 shadow-sm ring-1',
                    )}
                  >
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          'hover:bg-sidebar-muted/60 flex w-full items-center justify-between rounded-2xl px-2.5 py-2.5 text-left transition-colors',
                          isActive && 'bg-sidebar-muted/85',
                        )}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className={cn(
                              'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors',
                              isActive ? 'bg-primary text-primary-foreground' : 'text-sidebar-foreground/78',
                            )}
                          >
                            <section.icon className="h-4.5 w-4.5" />
                          </div>
                          <p className="text-sidebar-foreground truncate text-sm font-semibold">{section.label}</p>
                        </div>
                        <ChevronRight
                          className={cn(
                            'text-sidebar-foreground/75 h-4 w-4 shrink-0 transition-transform',
                            isOpen && 'rotate-90',
                          )}
                        />
                      </button>
                    </CollapsibleTrigger>

                    <CollapsibleContent className="border-sidebar-border/70 mr-1 ml-5 space-y-1.5 border-l pt-1 pb-1 pl-4">
                      {section.items.map((item) => (
                        <NavLink
                          key={item.id}
                          to={item.path}
                          onClick={() => setOpen(false)}
                          className={() =>
                            cn(
                              'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors',
                              isNavigationItemActive(currentLocation, item)
                                ? 'bg-primary text-primary-foreground'
                                : 'text-sidebar-foreground/90 hover:bg-sidebar-muted/70 hover:text-sidebar-foreground',
                            )
                          }
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          <span className="min-w-0 truncate font-medium">{item.label}</span>
                        </NavLink>
                      ))}
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
